import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Download, Upload, Archive, ChevronDown, ChevronRight, FileJson, Plus, Minus, ArrowRight, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Template Backup / Restore panel.
 *
 * Registry-driven — a new template type shows up automatically once
 * the backend registers it. Each kind gets a collapsible row with:
 *   • Export All → ZIP
 *   • Export Selected (checkbox picker)
 *   • Export Single (per-item download button)
 *
 * The whole-app Import + Export-All controls sit at the top so
 * bulk backups are one click away.
 */
const TemplateBackupPanel = () => {
  const { t } = useTranslation();
  const [kinds, setKinds] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [items, setItems] = useState({});         // { kind: [{id, name, ref}] }
  const [selected, setSelected] = useState({});   // { kind: Set(ids) }
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  // Diff-preview state: after the user picks files we run a dry-run
  // preview and show the modal. Applying the import stashes the file
  // objects so we don't force the user to re-pick.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [pendingFiles, setPendingFiles] = useState(null);
  // Rollback snapshot state — refreshed after any import and every
  // 30s while a live snapshot exists so the countdown stays accurate.
  const [rollback, setRollback] = useState(null);

  const refreshRollback = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/templates/backup/rollback/latest`);
      setRollback(r.data?.snapshot || null);
    } catch (e) { /* silently ignore — the button just won't show */ }
  }, []);

  // Poll while a snapshot is alive so the "N minutes left" chip stays fresh.
  useEffect(() => {
    refreshRollback();
    const iv = setInterval(refreshRollback, 30000);
    return () => clearInterval(iv);
  }, [refreshRollback]);

  const reloadRegistry = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/templates/backup/registry`);
      setKinds(r.data?.kinds || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load template registry');
    }
  }, []);

  useEffect(() => { reloadRegistry(); }, [reloadRegistry]);

  const toggleExpand = async (kind) => {
    const next = { ...expanded, [kind]: !expanded[kind] };
    setExpanded(next);
    if (next[kind] && !items[kind]) {
      try {
        const r = await axios.get(`${API}/templates/backup/list/${kind}`);
        setItems(prev => ({ ...prev, [kind]: r.data?.items || [] }));
      } catch (e) {
        toast.error(`Failed to list ${kind}: ${e.response?.data?.detail || e.message}`);
      }
    }
  };

  const toggleItem = (kind, id) => {
    setSelected(prev => {
      const set = new Set(prev[kind] || []);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...prev, [kind]: set };
    });
  };

  const selectAll = (kind, on) => {
    setSelected(prev => ({
      ...prev,
      [kind]: on ? new Set((items[kind] || []).map(i => i.id)) : new Set(),
    }));
  };

  // ── Download helpers ────────────────────────────────────────────
  const filenameFromDisposition = (disp, fallback) => {
    const m = /filename="([^"]+)"/i.exec(disp || '');
    return m ? m[1] : fallback;
  };

  const downloadBlob = async (url, fallbackName, opts = {}) => {
    const resp = await axios.get(url, { responseType: 'blob', ...opts });
    const name = filenameFromDisposition(resp.headers['content-disposition'], fallbackName);
    const blob = new Blob([resp.data]);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const exportSingle = async (kind, id, name) => {
    try {
      await downloadBlob(`${API}/templates/backup/export/single/${kind}/${id}`, `${kind}__${name || id}.json`);
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const exportZipForKind = async (kind, idsOptional) => {
    try {
      const resp = await axios.post(
        `${API}/templates/backup/export/zip`,
        { kind, ids: idsOptional },
        { responseType: 'blob' }
      );
      const name = filenameFromDisposition(resp.headers['content-disposition'], `${kind}.zip`);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([resp.data]));
      a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(`Export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const exportAllEverything = async () => {
    try {
      await downloadBlob(`${API}/templates/backup/export/zip-all`, `templates-all.zip`);
      toast.success(t('auto.set.downloadedFullBackup'));
    } catch (e) {
      toast.error(`Full export failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // ── Import: preview first, then apply on confirm ────────────────
  const handleImportPick = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    // Cache the FileList as a real array so it survives past the input.
    const filesArr = Array.from(fileList);
    setBusy(true);
    try {
      const form = new FormData();
      filesArr.forEach(f => form.append('files', f));
      const r = await axios.post(`${API}/templates/backup/import/preview`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewData(r.data);
      setPendingFiles(filesArr);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const applyImport = async () => {
    if (!pendingFiles) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('overwrite', overwrite ? 'true' : 'false');
      pendingFiles.forEach(f => form.append('files', f));
      const r = await axios.post(`${API}/templates/backup/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = r.data || {};
      toast.success(d.message || 'Import complete');
      if (d.errors?.length) {
        console.warn('Template import errors:', d.errors);
        toast.warning(`${d.errors.length} error(s) — see console for details`);
      }
      setPreviewOpen(false);
      setPreviewData(null);
      setPendingFiles(null);
      await reloadRegistry();
      await refreshRollback();
      setItems({});
    } catch (e) {
      toast.error(`Import failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const applyRollback = async () => {
    if (!rollback) return;
    if (!window.confirm(
      `Undo the last import?\n\n` +
      `${rollback.summary?.created || 0} created, ${rollback.summary?.updated || 0} updated will be reverted.\n` +
      `This action cannot itself be undone.`
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/templates/backup/rollback/${rollback.id}`);
      toast.success(r.data?.message || 'Rollback complete');
      await reloadRegistry();
      await refreshRollback();
      setItems({});
    } catch (e) {
      toast.error(`Rollback failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const rollbackMinutesLeft = rollback ? Math.max(0, Math.round((rollback.seconds_left || 0) / 60)) : 0;

  return (
    <div className="space-y-4" data-testid="template-backup-panel">
      {rollback && (
        <Card className="p-3 border-amber-300 bg-amber-50/60 flex items-center gap-3" data-testid="tpl-backup-rollback-banner">
          <Undo2 className="h-5 w-5 text-amber-700 shrink-0" />
          <div className="flex-1 text-xs">
            <div className="font-semibold text-amber-900">{t('auto.set.lastImportCanStillBeReverted')}</div>
            <div className="text-amber-800/80">
              {(rollback.summary?.created || 0)} created &middot; {(rollback.summary?.updated || 0)} updated
              &nbsp;— by <b>{rollback.taken_by}</b> &middot; expires in <b>{rollbackMinutesLeft} min</b>
              {rollback.affected?.length ? (
                <span className="ml-1 text-amber-700/70">({rollback.affected.slice(0, 2).map(a => a.name).join(', ')}{rollback.affected.length > 2 ? `, +${rollback.affected.length - 2} more` : ''})</span>
              ) : null}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-500 text-amber-800 hover:bg-amber-100"
            disabled={busy}
            onClick={applyRollback}
            data-testid="tpl-backup-rollback-btn"
          >
            <Undo2 className="h-4 w-4 mr-1" /> {t('auto.set.rollbackLastImport')}
          </Button>
        </Card>
      )}

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-base font-semibold">Template Backup &amp; Restore</div>
            <div className="text-xs text-gray-500">
              Export any template as JSON, ZIP a set, or restore from previously downloaded files.
              Files are named &nbsp;<code className="text-[10px]">kind__reference__name__date.json</code>&nbsp;
              so the folder alone is self-descriptive.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={exportAllEverything} data-testid="tpl-backup-export-all-btn">
              <Archive className="h-4 w-4 mr-1" /> {t('auto.set.exportAllAsZip')}
            </Button>
            <label className={`inline-flex items-center gap-2 border rounded px-3 py-1.5 text-sm cursor-pointer bg-white hover:bg-gray-50 ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
              <Upload className="h-4 w-4" /> {t('auto.set.importJsonZip')}
              <input
                type="file"
                multiple
                accept=".json,.zip,application/json,application/zip"
                className="hidden"
                onChange={(e) => { handleImportPick(e.target.files); e.target.value = ''; }}
                data-testid="tpl-backup-import-input"
              />
            </label>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Checkbox
            id="tpl-backup-overwrite"
            checked={overwrite}
            onCheckedChange={(v) => setOverwrite(!!v)}
            data-testid="tpl-backup-overwrite-toggle"
          />
          <label htmlFor="tpl-backup-overwrite" className="cursor-pointer">
            {t('auto.set.overwriteExistingTemplatesOnImportMatched')}
          </label>
        </div>
      </Card>

      <Card className="p-0 divide-y">
        {kinds.map(k => {
          const open = !!expanded[k.kind];
          const list = items[k.kind] || [];
          const sel = selected[k.kind] || new Set();
          return (
            <div key={k.kind} className="px-4 py-3" data-testid={`tpl-kind-${k.kind}`}>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => toggleExpand(k.kind)}
                  className="flex items-center gap-2 text-sm text-left hover:text-gray-900"
                  data-testid={`tpl-kind-toggle-${k.kind}`}
                >
                  {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <span className="font-medium">{k.label}</span>
                  <span className="text-xs text-gray-400">({k.count})</span>
                </button>
                <div className="flex gap-1">
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={k.count === 0}
                    onClick={() => exportZipForKind(k.kind)}
                    data-testid={`tpl-kind-export-all-${k.kind}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Export all ({k.count})
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={sel.size === 0}
                    onClick={() => exportZipForKind(k.kind, [...sel])}
                    data-testid={`tpl-kind-export-selected-${k.kind}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Export selected ({sel.size})
                  </Button>
                </div>
              </div>

              {open && (
                <div className="mt-2 pl-6 space-y-1">
                  {list.length === 0 && <div className="text-xs text-gray-400 italic">{t('auto.set.noTemplates')}</div>}
                  {list.length > 0 && (
                    <div className="flex gap-3 text-[11px] text-gray-500 mb-1">
                      <button type="button" onClick={() => selectAll(k.kind, true)} className="hover:text-gray-700">{t('auto.set.selectAll')}</button>
                      <button type="button" onClick={() => selectAll(k.kind, false)} className="hover:text-gray-700">{t('auto.set.clear')}</button>
                    </div>
                  )}
                  {list.map(it => (
                    <div key={it.id} className="flex items-center gap-2 text-xs group">
                      <Checkbox
                        checked={sel.has(it.id)}
                        onCheckedChange={() => toggleItem(k.kind, it.id)}
                        data-testid={`tpl-item-check-${k.kind}-${it.id}`}
                      />
                      <div className="flex-1 truncate">
                        <span className="font-medium">{it.name}</span>
                        {it.ref && <span className="text-gray-400 ml-2">({it.ref})</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => exportSingle(k.kind, it.id, it.ref || it.name)}
                        className="text-gray-400 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title={t('auto.set.downloadThisTemplateAsJson')}
                        data-testid={`tpl-item-download-${k.kind}-${it.id}`}
                      >
                        <FileJson className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      {/* ── Import diff-preview modal ─────────────────────────────── */}
      <Dialog open={previewOpen} onOpenChange={(v) => { if (!v) { setPreviewOpen(false); setPreviewData(null); setPendingFiles(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="text-base">{t('auto.set.reviewChangesBeforeImporting')}</DialogTitle>
            <div className="text-xs text-gray-500 flex gap-3 mt-1">
              <span><b>{previewData?.summary?.new ?? 0}</b> new</span>
              <span><b>{previewData?.summary?.changed ?? 0}</b> changed</span>
              <span><b>{previewData?.summary?.unchanged ?? 0}</b> unchanged</span>
              {previewData?.errors?.length ? <span className="text-red-600"><b>{previewData.errors.length}</b> errors</span> : null}
            </div>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 p-4 space-y-3">
            {(previewData?.previews || []).map((p, idx) => (
              <PreviewRow key={idx} preview={p} />
            ))}
            {(previewData?.errors || []).map((e, idx) => (
              <div key={`err-${idx}`} className="text-xs bg-red-50 border border-red-200 rounded p-2">
                <span className="font-semibold text-red-700">Error in {e.file}:</span> <span className="text-red-600">{e.error}</span>
              </div>
            ))}
            {(!previewData?.previews || previewData.previews.length === 0) && (previewData?.errors?.length ?? 0) === 0 && (
              <div className="text-sm text-gray-500 italic">{t('auto.set.noTemplatesDetectedInTheUploaded')}</div>
            )}
          </div>

          <DialogFooter className="p-4 border-t gap-2 flex-col sm:flex-row items-stretch sm:items-center">
            <label className="flex items-center gap-2 text-xs mr-auto">
              <Checkbox
                checked={overwrite}
                onCheckedChange={(v) => setOverwrite(!!v)}
                data-testid="tpl-backup-overwrite-toggle-modal"
              />
              <span>{t('auto.set.overwriteExistingRequiredToApplyChanges')}</span>
            </label>
            <Button size="sm" variant="ghost" onClick={() => setPreviewOpen(false)} disabled={busy}>{t('auto.set.cancel')}</Button>
            <Button
              size="sm"
              onClick={applyImport}
              disabled={busy || (previewData?.summary?.new === 0 && !overwrite && (previewData?.summary?.changed ?? 0) > 0)}
              data-testid="tpl-backup-apply-import-btn"
            >
              {busy ? t('auto.set.applying') : t('auto.set.applyImport')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Diff row for a single incoming template ──────────────────────
const PreviewRow = ({ preview }) => {
  const { t } = useTranslation();
  const diff = preview.diff || {};
  const isNew = diff.status === 'new';
  const noChange = diff.status === 'update' && !(diff.changes?.length) && !(diff.section_changes?.length);
  const badgeCls = isNew ? 'bg-green-100 text-green-800' : noChange ? 'bg-gray-100 text-gray-500' : 'bg-amber-100 text-amber-800';
  const badgeText = isNew ? 'NEW' : noChange ? t('auto.set.unchanged') : t('auto.set.update');
  return (
    <div className="border rounded p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeCls}`}>{badgeText}</span>
        <span className="text-xs text-gray-500 uppercase">{preview.label}</span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{preview.name}</div>
          {preview.ref && <div className="text-[11px] text-gray-400">{preview.ref}</div>}
        </div>
        <div className="text-[10px] text-gray-400 truncate max-w-[220px]" title={preview.file}>{preview.file}</div>
      </div>

      {!isNew && (diff.changes?.length > 0) && (
        <div className="text-xs space-y-1 pl-2 border-l-2 border-amber-200">
          {diff.changes.map((c, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="font-mono text-[10px] text-gray-500 w-24 shrink-0">{c.field}</span>
              {c.kind === 'scalar' ? (
                <div className="flex-1 flex items-center gap-1 min-w-0">
                  <span className="text-gray-500 line-through truncate">{String(c.before ?? '—')}</span>
                  <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />
                  <span className="font-medium truncate">{String(c.after ?? '—')}</span>
                </div>
              ) : c.kind === 'list' ? (
                <div className="flex-1 space-y-0.5">
                  <div className="text-gray-600">{c.before_count} → {c.after_count} items</div>
                  {(c.added || []).map((l, ii) => <div key={`a${ii}`} className="text-green-700 flex gap-1"><Plus className="h-3 w-3" /> {l}</div>)}
                  {(c.removed || []).map((l, ii) => <div key={`r${ii}`} className="text-red-700 flex gap-1"><Minus className="h-3 w-3" /> {l}</div>)}
                </div>
              ) : (
                <div className="flex-1 italic text-gray-400">{t('auto.set.complexChange')}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {!isNew && (diff.section_changes?.length > 0) && (
        <div className="text-xs pl-2 border-l-2 border-blue-200 space-y-2">
          {diff.section_changes.map((s, i) => (
            <div key={i}>
              <div className="font-semibold text-gray-700">{s.section} <span className="text-gray-400 font-normal">({s.before_items} → {s.after_items})</span></div>
              <div className="pl-3 space-y-0.5">
                {(s.added_items || []).map((l, ii) => <div key={`sa${ii}`} className="text-green-700 flex gap-1"><Plus className="h-3 w-3" /> {l}</div>)}
                {(s.removed_items || []).map((l, ii) => <div key={`sr${ii}`} className="text-red-700 flex gap-1"><Minus className="h-3 w-3" /> {l}</div>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {isNew && <div className="text-xs text-green-700 pl-2">{t('auto.set.willBeCreatedAsABrand')}</div>}
      {noChange && <div className="text-xs text-gray-400 italic pl-2">{t('auto.set.noDifferencesDetectedNothingToApply')}</div>}
    </div>
  );
};

export default TemplateBackupPanel;
